import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { buildSuggestion } from '../notes/mentionSuggestion';
import type { SuggestionRow } from '../notes/mentionSuggestion';
import { kindLabel, searchMentions } from '../notes/noteMentions';
import type { MentionSources } from '../notes/noteMentions';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * The `#` picker: deal, lead, order. Messages page only (plan-drop3-extras.md
 * H4), switched on through CommentEditor's `enableRecordPicker` prop.
 *
 * Deliberately NOT the CommentMention node. Picking a row inserts an ORDINARY
 * Hub link — plain `<a href>` text, nothing that notifies anyone — so the
 * preview card (plan-drop3-extras.md section 3) draws it exactly the way it
 * would draw a link somebody pasted or typed by hand (H1).
 */

export interface RecordLinkRow extends SuggestionRow {
  href: string;
}

/** deal, lead, order only — task and project stay on `@`, not `#` (H1). */
export async function searchRecordLinks(
  client: SupabaseClient,
  query: string,
  sources: MentionSources,
): Promise<RecordLinkRow[]> {
  const items = await searchMentions(client, query, sources);
  return items
    .filter((item) => item.kind === 'deal' || item.kind === 'lead' || item.kind === 'order')
    .map((item) => ({
      id: item.id,
      label: item.label,
      hint: item.hint,
      group: kindLabel(item.kind).toUpperCase(),
      // searchMentions gives an order the list page ('/cg-orders'), which is
      // right for an @ badge but opens no particular order and draws no
      // preview card (parseHubLink wants /cg-orders/<id>). A # link points at
      // the order itself.
      href: item.kind === 'order' ? `/cg-orders/${item.id}` : item.href,
    }));
}

export function recordLinkAttrs(row: RecordLinkRow): { label: string; href: string } {
  return { label: row.label, href: row.href };
}

const RECORD_LINK_PLUGIN_KEY = new PluginKey('recordLinkSuggestion');

export function RecordLinkSuggestion(config: {
  search: (query: string) => Promise<RecordLinkRow[]>;
}) {
  const popover = buildSuggestion<RecordLinkRow>({
    search: config.search,
    attrs: recordLinkAttrs,
    ariaLabel: 'Link a deal, lead or order',
  });

  return Extension.create({
    name: 'recordLinkSuggestion',
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: RECORD_LINK_PLUGIN_KEY,
          char: '#',
          allowSpaces: false,
          items: popover.items,
          render: popover.render,
          command: ({ editor, range, props }) => {
            const attrs = props as unknown as { label: string; href: string };
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: 'text',
                  marks: [{ type: 'link', attrs: { href: attrs.href } }],
                  text: attrs.label,
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
        }),
      ];
    },
  });
}
